import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { Spinner } from './Spinner'

type Variant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

const base =
  'inline-flex items-center justify-center gap-2 rounded font-semibold transition-colors ' +
  'disabled:cursor-not-allowed disabled:opacity-45 select-none'

const variants: Record<Variant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-dark active:bg-brand-darker',
  accent: 'bg-accent text-ink hover:bg-accent-dark active:bg-accent-dark',
  secondary:
    'bg-brand-light text-brand-dark hover:bg-[#d7efe1] active:bg-[#c9e9d7]',
  ghost: 'bg-transparent text-ink-soft hover:bg-black/5 active:bg-black/10',
  danger: 'bg-danger text-white hover:brightness-95',
}

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-10 px-5 text-sm',
  lg: 'h-12 px-6 text-[15px]',
}

interface CommonProps {
  variant?: Variant
  size?: Size
  /** Ocupa toda a largura disponível — padrão nos formulários. */
  block?: boolean
  loading?: boolean
  children: ReactNode
  className?: string
}

type ButtonProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps>

export function Button({
  variant = 'primary',
  size = 'md',
  block,
  loading,
  disabled,
  children,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      // `aria-busy` avisa o leitor de tela sem depender da troca visual do rótulo.
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(base, variants[variant], sizes[size], block && 'w-full', className)}
      {...props}
    >
      {loading && <Spinner size={16} className="shrink-0" />}
      {children}
    </button>
  )
}

interface ButtonLinkProps extends CommonProps {
  to: string
  /** Links externos abrem em nova aba com `rel` de segurança. */
  external?: boolean
}

export function ButtonLink({
  to,
  external,
  variant = 'primary',
  size = 'md',
  block,
  children,
  className,
}: ButtonLinkProps) {
  const classes = cn(base, variants[variant], sizes[size], block && 'w-full', className)

  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={classes}>
        {children}
      </a>
    )
  }
  return (
    <Link to={to} className={classes}>
      {children}
    </Link>
  )
}
